import React, { useState, useEffect } from 'react';
import {
  PhotoIcon,
  CloudArrowUpIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// API Base URL - detectar protocolo automaticamente (HTTPS quando site é HTTPS)
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  const currentHost = window.location.hostname;
  const protocol = window.location.protocol;

  // Se for um domínio (contém ponto e não é IP), usar o mesmo domínio sem porta
  // O Nginx vai interceptar /api/* e rotear para o backend
  if (currentHost.includes('.') && !currentHost.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return `${protocol}//${currentHost}`;
  }

  // Se for localhost ou IP, usar porta 8000
  return `${protocol}//${currentHost}:8000`;
};

const API_BASE_URL = getApiBaseUrl();

// Interfaces
interface SystemConfig {
  id?: number;
  company_logo?: string;
  company_name?: string;
  primary_color?: string;
  secondary_color?: string;
  company_cnpj?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_website?: string;
  proposal_validity_days?: number;
  proposal_auto_numbering?: boolean;
  contract_auto_generate?: boolean;
}

interface ProposalTemplate {
  id?: number;
  title: string;
  template_type: 'contractor_obligation' | 'client_obligation' | 'general_observation';
  content: string;
  is_active: boolean;
  is_default: boolean;
  order: number;
}

type TabType = 'company' | 'templates';

const SystemSettings: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('company');

  // Company config state
  const [config, setConfig] = useState<SystemConfig>({
    company_name: 'Lexpress Locações',
    primary_color: '#3B82F6',
    secondary_color: '#64748B',
    proposal_validity_days: 15,
    proposal_auto_numbering: true,
    contract_auto_generate: false
  });
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProposalTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<Partial<ProposalTemplate>>({
    title: '',
    template_type: 'contractor_obligation',
    content: '',
    is_active: true,
    is_default: false,
    order: 0
  });

  // General state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSystemConfig();
    loadTemplates();
  }, []);

  // ==================== HELPER FUNCTIONS ====================

  const getAuthToken = (): string | null => {
    const authTokensStr = localStorage.getItem('authTokens');
    if (!authTokensStr) return null;

    try {
      const authTokens = JSON.parse(authTokensStr);
      return authTokens.access;
    } catch (err) {
      console.error('Erro ao parsear authTokens:', err);
      return null;
    }
  };

  // ==================== COMPANY DATA FUNCTIONS ====================

  const loadSystemConfig = async () => {
    try {
      setLoadingConfig(true);
      setError('');

      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/system/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        if (data.company_logo_url) {
          setLogoPreview(data.company_logo_url);
        }
      } else {
        const errorData = await response.json();
        console.error('Erro na API:', errorData);
        setError(errorData.error || errorData.detail || 'Erro ao carregar configurações');
      }
    } catch (err) {
      console.error('Erro ao carregar configuração:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 2MB');
        return;
      }

      setLogo(file);
      setError('');

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfigChange = (field: keyof SystemConfig, value: string | number | boolean) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    try {
      setLoadingConfig(true);
      setError('');
      setSuccess('');

      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }
      const formData = new FormData();

      // Add all config fields
      Object.keys(config).forEach(key => {
        const value = config[key as keyof SystemConfig];
        if (value !== undefined && value !== null && key !== 'company_logo') {
          formData.append(key, value.toString());
        }
      });

      // Add logo if changed
      if (logo) {
        formData.append('company_logo', logo);
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/system/${config.id || 1}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setSuccess('Configurações salvas com sucesso!');
        setLogo(null);

        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('system-config-updated', {
          detail: data
        }));
      } else {
        setError('Erro ao salvar configurações');
      }
    } catch (err: any) {
      console.error('Erro ao salvar:', err);
      setError('Erro ao salvar configurações');
    } finally {
      setLoadingConfig(false);
    }
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview('');
  };

  // ==================== TEMPLATES FUNCTIONS ====================

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      setError('');

      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/templates/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // A API retorna um objeto paginado com 'results'
        const templatesList = Array.isArray(data) ? data : (data.results || []);
        console.log('Templates carregados:', templatesList.length);
        console.log('Por tipo:', {
          contractor: templatesList.filter((t: any) => t.template_type === 'contractor_obligation').length,
          client: templatesList.filter((t: any) => t.template_type === 'client_obligation').length,
          observation: templatesList.filter((t: any) => t.template_type === 'general_observation').length
        });
        setTemplates(templatesList);
      } else {
        const errorData = await response.json();
        console.error('Erro na API:', errorData);
        setError(errorData.error || errorData.detail || 'Erro ao carregar templates');
      }
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateFormChange = (field: keyof ProposalTemplate, value: any) => {
    setTemplateForm(prev => ({ ...prev, [field]: value }));
  };

  const openTemplateModal = (template?: ProposalTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm(template);
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        title: '',
        template_type: 'contractor_obligation',
        content: '',
        is_active: true,
        is_default: false,
        order: 0
      });
    }
    setShowTemplateModal(true);
  };

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setEditingTemplate(null);
    setTemplateForm({
      title: '',
      template_type: 'contractor_obligation',
      content: '',
      is_active: true,
      is_default: false,
      order: 0
    });
  };

  const handleSaveTemplate = async () => {
    try {
      setError('');
      setSuccess('');

      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const url = editingTemplate
        ? `${API_BASE_URL}/api/v1/settings/templates/${editingTemplate.id}/`
        : `${API_BASE_URL}/api/v1/settings/templates/`;

      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        await loadTemplates();
        closeTemplateModal();
        setSuccess(editingTemplate ? 'Template atualizado com sucesso!' : 'Template criado com sucesso!');
      } else {
        const data = await response.json();
        setError(data.detail || 'Erro ao salvar template');
      }
    } catch (err) {
      console.error('Erro ao salvar template:', err);
      setError('Erro ao salvar template');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) {
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        setError('Usuário não autenticado');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/settings/templates/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadTemplates();
        setSuccess('Template excluído com sucesso!');
      } else {
        setError('Erro ao excluir template');
      }
    } catch (err) {
      console.error('Erro ao excluir template:', err);
      setError('Erro ao excluir template');
    }
  };

  const getTemplateTypeLabel = (type: string) => {
    const labels = {
      'contractor_obligation': 'Obrigação da Contratada',
      'client_obligation': 'Obrigação da Contratante',
      'general_observation': 'Observação Geral'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getTemplatesByType = (type: string) => {
    return templates.filter(t => t.template_type === type).sort((a, b) => a.order - b.order);
  };

  // ==================== RENDER ====================

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow border">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-base font-bold text-gray-900">
            Configurações do Sistema
          </h1>
          <p className="text-xs text-gray-600 mt-1">
            Configure o sistema, dados da empresa e templates de propostas
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('company')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'company'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dados da Empresa
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'templates'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Templates de Propostas
            </button>
          </nav>
        </div>

        {/* Mensagens globais */}
        <div className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'company' && (
          <div className="p-6 space-y-6">
            {/* Logo da Empresa */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Logo da Empresa
              </h3>

              <div className="space-y-4">
                {logoPreview && (
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-16 w-auto max-w-xs object-contain bg-gray-50 border border-gray-200 rounded-lg p-2"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Logo atual</p>
                      <button
                        onClick={removeLogo}
                        className="mt-1 text-sm text-red-600 hover:text-red-700"
                      >
                        Remover logo
                      </button>
                    </div>
                  </div>
                )}

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label htmlFor="logo-upload" className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          {logoPreview ? 'Alterar logo' : 'Adicionar logo da empresa'}
                        </span>
                        <span className="mt-1 block text-xs text-gray-500">
                          PNG, JPG, SVG até 2MB
                        </span>
                      </label>
                      <input
                        id="logo-upload"
                        name="logo-upload"
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleLogoChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Nome da Empresa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Empresa
              </label>
              <input
                type="text"
                value={config.company_name || ''}
                onChange={(e) => handleConfigChange('company_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nome da sua empresa"
              />
            </div>

            {/* Cores do Sistema */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cor Primária
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.primary_color || '#3B82F6'}
                    onChange={(e) => handleConfigChange('primary_color', e.target.value)}
                    className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.primary_color || '#3B82F6'}
                    onChange={(e) => handleConfigChange('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cor Secundária
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={config.secondary_color || '#64748B'}
                    onChange={(e) => handleConfigChange('secondary_color', e.target.value)}
                    className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.secondary_color || '#64748B'}
                    onChange={(e) => handleConfigChange('secondary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="#64748B"
                  />
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                onClick={loadSystemConfig}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveConfig}
                disabled={loadingConfig}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loadingConfig && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                <CloudArrowUpIcon className="h-4 w-4 mr-1.5" />
                Salvar Configurações
              </button>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="p-6 space-y-6">
            {/* Header com botão adicionar */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Templates de Obrigações e Observações
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  Gerencie templates que podem ser usados em propostas
                </p>
              </div>
              <button
                onClick={() => openTemplateModal()}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Novo Template
              </button>
            </div>

            {loadingTemplates ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-xs text-gray-600 mt-2">Carregando templates...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Obrigações da Contratada */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Obrigações da Contratada
                  </h4>
                  <div className="space-y-2">
                    {getTemplatesByType('contractor_obligation').length === 0 ? (
                      <p className="text-xs text-gray-500 italic">Nenhum template cadastrado</p>
                    ) : (
                      getTemplatesByType('contractor_obligation').map(template => (
                        <div key={template.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h5 className="font-medium text-gray-900">{template.title}</h5>
                              {template.is_default && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                  Padrão
                                </span>
                              )}
                              {!template.is_active && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                  Inativo
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{template.content}</p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => openTemplateModal(template)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Editar"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id!)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Excluir"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Obrigações da Contratante */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Obrigações da Contratante
                  </h4>
                  <div className="space-y-2">
                    {getTemplatesByType('client_obligation').length === 0 ? (
                      <p className="text-xs text-gray-500 italic">Nenhum template cadastrado</p>
                    ) : (
                      getTemplatesByType('client_obligation').map(template => (
                        <div key={template.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h5 className="font-medium text-gray-900">{template.title}</h5>
                              {template.is_default && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                  Padrão
                                </span>
                              )}
                              {!template.is_active && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                  Inativo
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{template.content}</p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => openTemplateModal(template)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Editar"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id!)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Excluir"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Observações Gerais */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Observações Gerais
                  </h4>
                  <div className="space-y-2">
                    {getTemplatesByType('general_observation').length === 0 ? (
                      <p className="text-xs text-gray-500 italic">Nenhum template cadastrado</p>
                    ) : (
                      getTemplatesByType('general_observation').map(template => (
                        <div key={template.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h5 className="font-medium text-gray-900">{template.title}</h5>
                              {template.is_default && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                  Padrão
                                </span>
                              )}
                              {!template.is_active && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                  Inativo
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{template.content}</p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => openTemplateModal(template)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Editar"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id!)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Excluir"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-900">
                {editingTemplate ? 'Editar Template' : 'Novo Template'}
              </h2>
              <button
                onClick={closeTemplateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  value={templateForm.title || ''}
                  onChange={(e) => handleTemplateFormChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Pagamento em dia"
                />
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  value={templateForm.template_type || 'contractor_obligation'}
                  onChange={(e) => handleTemplateFormChange('template_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="contractor_obligation">Obrigação da Contratada</option>
                  <option value="client_obligation">Obrigação da Contratante</option>
                  <option value="general_observation">Observação Geral</option>
                </select>
              </div>

              {/* Conteúdo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conteúdo *
                </label>
                <textarea
                  value={templateForm.content || ''}
                  onChange={(e) => handleTemplateFormChange('content', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descreva o conteúdo do template..."
                />
              </div>

              {/* Ordem */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordem
                </label>
                <input
                  type="number"
                  value={templateForm.order || 0}
                  onChange={(e) => handleTemplateFormChange('order', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={templateForm.is_active || false}
                    onChange={(e) => handleTemplateFormChange('is_active', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Ativo</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={templateForm.is_default || false}
                    onChange={(e) => handleTemplateFormChange('is_default', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Padrão (adicionar automaticamente em novas propostas)
                  </span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <button
                onClick={closeTemplateModal}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveTemplate}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingTemplate ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
